import { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Menu, X, Sparkles, ArrowRight, LayoutDashboard } from "lucide-react";

/**
 * TopNav — sticky, responsive top navigation used on the public Landing
 * page AND above the authenticated Dashboard.
 *
 * Behavior:
 *   - "How it Works" always routes to `/#how-it-works` and smooth-scrolls
 *     to the section (handled via useLocation hash sync below).
 *   - "Analogy Library", "AI Tutor", "Flashcards" route into the app; if
 *     the visitor isn't logged in the app router will send them to /auth.
 *   - Right side swaps between [Log In | Get Started] (anon) and
 *     [Dashboard | Sign Out] (auth).
 *   - Mobile: hamburger opens a full-width dropdown panel.
 */
const LINKS = [
  { label: "Home", to: "/", testid: "topnav-home" },
  { label: "How it Works", to: "/#how-it-works", testid: "topnav-how", scroll: "how-it-works" },
  { label: "Analogy Library", to: "/app/library", testid: "topnav-library" },
  { label: "AI Tutor", to: "/app/coach/history", testid: "topnav-tutor" },
  { label: "Flashcards", to: "/app/flashcards", testid: "topnav-flashcards" },
  { label: "Community", to: "/app/community", testid: "topnav-community" },
  { label: "Physiology", to: "/app/physiology", testid: "topnav-physiology" },
  { label: "FAQ", to: "/faq", testid: "topnav-faq" },
];

export default function TopNav() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  // Smooth-scroll when arriving at /#how-it-works
  useEffect(() => {
    if (location.pathname === "/" && location.hash) {
      const id = location.hash.replace("#", "");
      const el = document.getElementById(id);
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
      }
    }
  }, [location.pathname, location.hash]);

  const close = () => setOpen(false);

  return (
    <nav
      className="sticky top-0 z-40 backdrop-blur-md border-b"
      style={{ background: "color-mix(in oklab, var(--paper) 88%, transparent)", borderColor: "var(--border)" }}
      data-testid="top-nav"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-3">
        <Link to={user ? "/app" : "/"} className="flex items-center gap-2 shrink-0" data-testid="topnav-brand">
          <img src="/picturemed-logo.png" alt="PictureMed" className="h-9 w-auto" />
        </Link>

        {/* Desktop links */}
        <div className="hidden lg:flex items-center gap-1">
          {LINKS.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              onClick={close}
              className="text-sm font-medium px-3 py-2 rounded-lg hover:bg-[var(--muted)] transition-colors"
              style={{ color: "var(--text)" }}
              data-testid={l.testid}
            >
              {l.label}
            </Link>
          ))}
        </div>

        {/* Right CTAs — desktop */}
        <div className="hidden lg:flex items-center gap-2 shrink-0">
          {user ? (
            <>
              <Link
                to="/app"
                className="pm-btn-secondary inline-flex items-center gap-1.5 text-sm"
                data-testid="topnav-dashboard-btn"
              >
                <LayoutDashboard size={14} /> Dashboard
              </Link>
              <Link
                to="/app/security"
                className="text-sm font-medium px-3 py-2 rounded-lg hover:bg-[var(--muted)]"
                style={{ color: "var(--text-muted)" }}
                data-testid="topnav-security-btn"
              >
                Security
              </Link>
              <button
                onClick={() => { logout(); nav("/"); }}
                className="text-sm font-medium px-3 py-2 rounded-lg hover:bg-[var(--muted)]"
                style={{ color: "var(--text-muted)" }}
                data-testid="topnav-signout-btn"
              >
                Sign out
              </button>
            </>
          ) : (
            <>
              <Link
                to="/auth"
                className="text-sm font-medium px-3 py-2 rounded-lg hover:bg-[var(--muted)]"
                style={{ color: "var(--text)" }}
                data-testid="topnav-login-btn"
              >
                Log In
              </Link>
              <Link
                to="/auth"
                className="pm-btn-primary inline-flex items-center gap-1.5 text-sm"
                data-testid="topnav-getstarted-btn"
              >
                <Sparkles size={14} /> Get Started <ArrowRight size={14} />
              </Link>
            </>
          )}
        </div>

        {/* Mobile hamburger */}
        <button
          onClick={() => setOpen((v) => !v)}
          className="lg:hidden p-2 rounded-lg hover:bg-[var(--muted)]"
          aria-label="Toggle menu"
          data-testid="topnav-mobile-toggle"
        >
          {open ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>

      {/* Mobile panel */}
      {open && (
        <div
          className="lg:hidden border-t px-4 py-3 space-y-1"
          style={{ borderColor: "var(--border)", background: "var(--paper)" }}
          data-testid="topnav-mobile-panel"
        >
          {LINKS.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              onClick={close}
              className="block px-3 py-2.5 rounded-lg text-sm font-medium hover:bg-[var(--muted)]"
              style={{ color: "var(--text)" }}
              data-testid={`${l.testid}-mobile`}
            >
              {l.label}
            </Link>
          ))}
          <div className="pt-2 mt-2 border-t flex flex-col gap-2" style={{ borderColor: "var(--border)" }}>
            {user ? (
              <>
                <Link
                  to="/app"
                  onClick={close}
                  className="pm-btn-secondary text-sm w-full text-center"
                >
                  Dashboard
                </Link>
                <Link
                  to="/app/security"
                  onClick={close}
                  className="text-sm w-full text-center px-3 py-2 rounded-lg hover:bg-[var(--muted)]"
                  style={{ color: "var(--text-muted)" }}
                  data-testid="topnav-security-btn-mobile"
                >
                  Security
                </Link>
                <button
                  onClick={() => { close(); logout(); nav("/"); }}
                  className="pm-btn-primary text-sm w-full"
                >
                  Sign out
                </button>
              </>
            ) : (
              <>
                <Link
                  to="/auth"
                  onClick={close}
                  className="pm-btn-secondary text-sm w-full text-center"
                  data-testid="topnav-login-btn-mobile"
                >
                  Log In
                </Link>
                <Link
                  to="/auth"
                  onClick={close}
                  className="pm-btn-primary text-sm w-full text-center inline-flex items-center justify-center gap-1.5"
                  data-testid="topnav-getstarted-btn-mobile"
                >
                  Get Started <ArrowRight size={14} />
                </Link>
              </>
            )}
          </div>
        </div>
      )}
    </nav>
  );
}
